package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.files.File

@Composable
fun FormField(label: String, content: @Composable () -> Unit) {
    val colors = AppTheme.colors
    Div({
        style { marginBottom(Spacing.lg) }
    }) {
        Div({
            style {
                display(DisplayStyle.Block)
                fontSize(Typography.base)
                fontWeight("600")
                color(Color(colors.text))
                marginBottom(Spacing.sm)
                themeTransition()
            }
        }) { Text(label) }
        content()
    }
}

@Composable
fun InfoRow(label: String, value: String) {
    val colors = AppTheme.colors
    Div({
        style {
            display(DisplayStyle.Flex)
            marginBottom(Spacing.sm)
            fontSize(Typography.base)
        }
    }) {
        Span({
            style {
                fontWeight("600")
                color(Color(colors.text))
                minWidth(120.px)
            }
        }) { Text("$label:") }
        Span({
            style {
                color(Color(colors.textMuted))
                flex("1")
                property("word-break", "break-word")
            }
        }) { Text(value) }
    }
}

@Composable
fun TabButton(label: String, isActive: Boolean, onClick: () -> Unit) {
    val colors = AppTheme.colors
    Button({
        onClick { onClick() }
        style {
            padding(Spacing.md, Spacing.xxl)
            backgroundColor(if (isActive) Color(colors.surface) else Color("transparent"))
            border(0.px)
            property("border-bottom", if (isActive) "2px solid ${colors.primary}" else "2px solid transparent")
            cursor("pointer")
            fontSize(Typography.base)
            fontWeight(if (isActive) "600" else "400")
            color(Color(if (isActive) colors.primary else colors.textMuted))
            property("transition", "all 0.2s")
        }
    }) { Text(label) }
}

@Composable
fun MenuButton(label: String, onClick: () -> Unit) {
    val colors = AppTheme.colors
    Button({
        onClick { onClick() }
        style {
            width(100.percent)
            padding(Spacing.md, Spacing.lg)
            backgroundColor(Color("transparent"))
            color(Color(colors.text))
            border(0.px)
            textAlign("left")
            cursor("pointer")
            fontSize(Typography.base)
            property("transition", "background-color 0.2s")
        }
        onMouseOver { it.target.asDynamic().style.backgroundColor = colors.surfaceHover }
        onMouseOut { it.target.asDynamic().style.backgroundColor = "transparent" }
    }) { Text(label) }
}

@Composable
fun ActionButton(
    label: String,
    backgroundColor: String,
    hoverColor: String,
    onClick: () -> Unit
) {
    Button({
        onClick { onClick() }
        style {
            padding(Spacing.sm, Spacing.md)
            backgroundColor(Color(backgroundColor))
            color(Color("#ffffff"))
            border(0.px)
            borderRadius(BorderRadius.sm)
            fontSize(Typography.sm)
            fontWeight("500")
            cursor("pointer")
            property("transition", "background-color 0.2s")
        }
        onMouseOver { it.target.asDynamic().style.backgroundColor = hoverColor }
        onMouseOut { it.target.asDynamic().style.backgroundColor = backgroundColor }
    }) {
        Text(label)
    }
}

@Composable
fun StatusBadge(
    text: String,
    backgroundColor: String,
    textColor: String
) {
    Span({
        style {
            padding(Spacing.xs, Spacing.sm)
            backgroundColor(Color(backgroundColor))
            color(Color(textColor))
            borderRadius(BorderRadius.sm)
            fontSize(Typography.xs)
            fontWeight("600")
        }
    }) { Text(text) }
}

@Composable
fun LoadingIndicator(message: String = "Loading...") {
    val colors = AppTheme.colors
    Div({
        style {
            padding(40.px)
            textAlign("center")
            color(Color(colors.textMuted))
        }
    }) { Text(message) }
}

@Composable
fun EmptyState(message: String) {
    val colors = AppTheme.colors
    Div({
        style {
            padding(40.px)
            textAlign("center")
            color(Color(colors.textMuted))
        }
    }) { Text(message) }
}

@Composable
fun ErrorMessage(message: String) {
    val colors = AppTheme.colors
    Div({
        style { errorMessageStyle(colors) }
    }) { Text(message) }
}

fun formatTimestamp(isoString: String): String {
    return try {
        val date = kotlin.js.Date(isoString)
        if (date.getTime().isNaN()) return isoString
        val month = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
        val m = month[date.getMonth()]
        val d = date.getDate()
        val y = date.getFullYear()
        val h = date.getHours().toString().padStart(2, '0')
        val min = date.getMinutes().toString().padStart(2, '0')
        "$m $d, $y at $h:$min"
    } catch (e: Exception) {
        isoString
    }
}

@Composable
fun ImageUpload(
    currentImageUrl: String?,
    originalImageUrl: String?,
    isLinked: Boolean,
    hasCustomImage: Boolean,
    label: String,
    onUpload: suspend (File) -> Unit,
    onRemove: suspend () -> Unit
) {
    val colors = AppTheme.colors
    var isUploading by remember { mutableStateOf(false) }
    var uploadError by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    val displayImageUrl = currentImageUrl ?: originalImageUrl

    FormField(label) {
        Div({
            style {
                display(DisplayStyle.Flex)
                property("flex-direction", "column")
                property("gap", "12px")
            }
        }) {
            if (displayImageUrl != null) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        property("flex-direction", "column")
                        property("gap", "8px")
                    }
                }) {
                    Img(displayImageUrl) {
                        style {
                            width(200.px)
                            height(200.px)
                            borderRadius(BorderRadius.lg)
                            property("object-fit", "cover")
                            border(1.px, LineStyle.Solid, Color(colors.border))
                        }
                    }

                    if (!hasCustomImage && originalImageUrl != null) {
                        Span({
                            style {
                                fontSize(Typography.sm)
                                color(Color(colors.textMuted))
                                fontStyle("italic")
                            }
                        }) {
                            Text(if (isLinked) "Using RSS feed image" else "Original image")
                        }
                    } else if (hasCustomImage) {
                        Span({
                            style {
                                fontSize(Typography.sm)
                                color(Color(colors.success))
                                fontWeight("500")
                            }
                        }) {
                            Text("Custom image uploaded")
                        }
                    }
                }
            } else {
                Div({
                    style {
                        width(200.px)
                        height(200.px)
                        backgroundColor(Color(colors.surfaceHover))
                        borderRadius(BorderRadius.lg)
                        display(DisplayStyle.Flex)
                        property("align-items", "center")
                        property("justify-content", "center")
                        color(Color(colors.textMuted))
                        fontSize(Typography.base)
                        border(2.px, LineStyle.Dashed, Color(colors.border))
                    }
                }) {
                    Text("No image")
                }
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    property("gap", "8px")
                    property("align-items", "center")
                }
            }) {
                Input(InputType.File) {
                    id("image-upload-${label.replace(" ", "-")}")
                    attr("accept", "image/*")
                    if (isUploading) disabled()
                    onChange { event ->
                        val input = event.target as org.w3c.dom.HTMLInputElement
                        val file = input.files?.item(0)
                        if (file != null) {
                            scope.launch {
                                isUploading = true
                                uploadError = null
                                try {
                                    onUpload(file.unsafeCast<File>())
                                    input.value = ""
                                } catch (e: Exception) {
                                    uploadError = e.message ?: "Upload failed"
                                } finally {
                                    isUploading = false
                                }
                            }
                        }
                    }
                    style {
                        display(DisplayStyle.None)
                    }
                }

                Label(forId = "image-upload-${label.replace(" ", "-")}") {
                    Button({
                        attr("type", "button")
                        if (isUploading) disabled()
                        onClick { event ->
                            event.preventDefault()
                            kotlinx.browser.document.getElementById("image-upload-${label.replace(" ", "-")}")?.asDynamic()?.click()
                        }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            backgroundColor(Color(colors.primary))
                            color(Color("#ffffff"))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            fontSize(Typography.base)
                            fontWeight("500")
                            cursor(if (isUploading) "not-allowed" else "pointer")
                            property("transition", "background-color 0.2s")
                            if (isUploading) {
                                opacity(0.6)
                            }
                        }
                        onMouseOver {
                            if (!isUploading) {
                                it.target.asDynamic().style.backgroundColor = colors.primaryHover
                            }
                        }
                        onMouseOut {
                            it.target.asDynamic().style.backgroundColor = colors.primary
                        }
                    }) {
                        Text(if (isUploading) "Uploading..." else if (hasCustomImage) "Change Image" else "Upload Image")
                    }
                }

                if (hasCustomImage && !isUploading) {
                    Button({
                        onClick {
                            scope.launch {
                                isUploading = true
                                uploadError = null
                                try {
                                    onRemove()
                                } catch (e: Exception) {
                                    uploadError = e.message ?: "Failed to remove image"
                                } finally {
                                    isUploading = false
                                }
                            }
                        }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            backgroundColor(Color(colors.danger))
                            color(Color("#ffffff"))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            fontSize(Typography.base)
                            fontWeight("500")
                            cursor("pointer")
                            property("transition", "background-color 0.2s")
                        }
                    }) {
                        Text("Remove")
                    }

                    if (isLinked && originalImageUrl != null) {
                        Span({
                            style {
                                fontSize(Typography.sm)
                                color(Color(colors.textMuted))
                                fontStyle("italic")
                            }
                        }) {
                            Text("(will revert to RSS image)")
                        }
                    }
                }
            }

            Div({
                style {
                    fontSize(Typography.sm)
                    color(Color(colors.textMuted))
                }
            }) {
                Text("Accepted formats: JPG, PNG, GIF, WebP (max 10MB)")
            }

            if (isUploading) {
                Div({
                    style {
                        fontSize(Typography.base)
                        color(Color(colors.primary))
                        fontWeight("500")
                    }
                }) {
                    Text("Processing...")
                }
            }

            uploadError?.let { error ->
                Div({
                    style {
                        errorMessageStyle(colors)
                    }
                }) {
                    Text(error)
                }
            }
        }
    }
}
